import { ComponentProps } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCircleCheck,
  faCircleXmark,
  faClock,
  faSpinner,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

type Props = {
  statusCode: string;
  isCancelling?: boolean;
  /**
   * Render a short, single-word label (e.g. "Timeout") and surface the full
   * label on hover. Use on narrow surfaces such as the canvas-side decision
   * history pane.
   */
  compact?: boolean;
};

type DecisiontStatus = "success" | "pending" | "error";
export const getDecisionStatus = (statusCode: string): DecisiontStatus => {
  if (statusCode === "200") return "success";
  if (statusCode === "202") return "pending";
  return "error";
};

export type DecisionStatusCode =
  (typeof POSSIBLE_DECISION_STATUS_CODES)[number]["key"];

export const POSSIBLE_DECISION_STATUS_CODES = [
  { key: "200", value: "Successful" },
  { key: "202", value: "Pending" },
  { key: "408", value: "Failed: timeout (408)" },
  { key: "409", value: "Failed: output schema (409)" },
  { key: "410", value: "Canceled" },
  { key: "422", value: "Failed: input schema (422)" },
  { key: "424", value: "Failed: logic (424)" },
  { key: "500", value: "Failed: system (500)" },
  { key: "502", value: "Failed: integration system (502)" },
] as const;

const COMPACT_DECISION_STATUS_LABELS: Record<DecisionStatusCode, string> = {
  "200": "Successful",
  "202": "Pending",
  "408": "Timeout",
  "409": "Output schema",
  "410": "Canceled",
  "422": "Input schema",
  "424": "Logic",
  "500": "System",
  "502": "Provider",
};

type StatusVisuals = {
  icon: IconProp;
  iconVariant: ComponentProps<typeof Icon>["variant"];
  textClassName?: string;
  fullLabel: string;
  compactLabel: string;
};

const getStatusVisuals = (
  statusCode: string,
  isCancelling: boolean,
): StatusVisuals => {
  if (isCancelling && statusCode === "202") {
    return {
      icon: faSpinner,
      iconVariant: "default",
      textClassName: "text-gray-600",
      fullLabel: "Cancelling...",
      compactLabel: "Cancelling...",
    };
  }
  if (statusCode === "200") {
    return {
      icon: faCircleCheck,
      iconVariant: "success",
      fullLabel: "Successful",
      compactLabel: COMPACT_DECISION_STATUS_LABELS["200"],
    };
  }
  if (statusCode === "410") {
    return {
      icon: faCircleXmark,
      iconVariant: "warning",
      fullLabel: "Canceled",
      compactLabel: COMPACT_DECISION_STATUS_LABELS["410"],
    };
  }
  if (statusCode === "202") {
    return {
      icon: faClock,
      iconVariant: "active",
      fullLabel: "Pending",
      compactLabel: COMPACT_DECISION_STATUS_LABELS["202"],
    };
  }
  const fullLabel =
    POSSIBLE_DECISION_STATUS_CODES.find((entry) => entry.key === statusCode)
      ?.value ?? `Error (${statusCode})`;
  const compactLabel =
    (COMPACT_DECISION_STATUS_LABELS as Record<string, string>)[statusCode] ??
    "Error";
  return { icon: faWarning, iconVariant: "error", fullLabel, compactLabel };
};

export const SharedDecisionHistoryStatus: React.FC<Props> = ({
  statusCode,
  isCancelling,
  compact,
}) => {
  const { icon, iconVariant, textClassName, fullLabel, compactLabel } =
    getStatusVisuals(statusCode, Boolean(isCancelling));

  return (
    <Tooltip
      asChild
      disabled={!compact || compactLabel === fullLabel}
      placement="top"
      title={fullLabel}
    >
      <div className="flex items-center gap-1 whitespace-nowrap">
        <Icon icon={icon} size="xs" variant={iconVariant} />
        <span className={twJoin("capitalize", textClassName)}>
          {compact ? compactLabel : fullLabel}
        </span>
      </div>
    </Tooltip>
  );
};
